// Content for the service pages (rendered by <ServicePage />).

export interface ServiceCapability {
  title: string;
  text: string;
}

// A stack item is either a plain chip (string) or a logo tile. Tiles without a logo file show a monogram badge.
export type ServiceStackItem = string | { name: string; logo?: string; badge?: string; color?: string };

export interface ServiceStackGroup {
  label: string;
  items: ServiceStackItem[];
}

export interface ServiceData {
  name: string;
  banner: string;
  subtitle: string;
  intro: string;
  flow: string[];
  capabilitiesTitle: string;
  capabilities: ServiceCapability[];
  process?: { title: string; steps: ServiceCapability[] };
  methods?: { title: string; items: ServiceCapability[] };
  stack?: { title: string; groups: ServiceStackGroup[] };
  why?: { title: string; items: ServiceCapability[] };
  faq: { q: string; a: string }[];
  cta: string;
}

const TECHNIQUES: ServiceCapability[] = [
  { title: 'Machine Learning Algorithms', text: 'Decision trees, regression, random forests, SVM and KNN.' },
  { title: 'Deep Learning', text: 'Neural networks, CNNs, RNNs and autoencoders.' },
  { title: 'Supervised Learning', text: 'Curated labeled data and tuned model architectures.' },
  { title: 'Unsupervised Learning', text: 'Patterns and relationships found in unlabeled data.' },
  { title: 'Transfer Learning', text: 'Pre-trained models fine-tuned on curated datasets.' },
  { title: 'Reinforcement Learning', text: 'Agents trained on environmental feedback using Markov Decision Processes.' },
  { title: 'Natural Language Processing', text: 'NLTK and SpaCy: tokenization, stemming and lemmatization.' },
  { title: 'Computer Vision', text: 'Feature extraction and image processing with OpenCV and TensorFlow.' },
];

const t = (name: string, logo?: string, badge?: string, color?: string) => ({ name, logo, badge, color });

const PLATFORM_STACK: ServiceStackGroup[] = [
  { label: 'Cloud Platforms', items: [t('AWS', 'aws'), t('Google Cloud', 'googlecloud'), t('Azure', 'azure')] },
  { label: 'AI Frameworks', items: [t('TensorFlow', 'tensorflow'), t('PyTorch', 'pytorch')] },
  { label: 'Integration & Deployment', items: [t('Docker', 'docker'), t('Kubernetes', 'kubernetes'), t('Databricks', 'databricks'), t('Airflow', 'airflow')] },
  { label: 'Programming Languages', items: [t('Python', 'python'), t('SQL', undefined, 'SQL', '#00437E'), t('Java', 'java'), t('JavaScript', 'javascript'), t('TypeScript', 'typescript')] },
  { label: 'Visualization', items: [t('Tableau', undefined, 'T', '#E97627'), t('Looker', 'looker'), t('Power BI', undefined, 'BI', '#F2C811')] },
  { label: 'Collaboration', items: [t('GitLab', 'gitlab'), t('Jupyter Lab', 'jupyter'), t('Jira', 'jira'), t('Confluence', 'confluence')] },
];

export const SERVICES: Record<string, ServiceData> = {
  aiml: {
    name: 'Artificial Intelligence And ML',
    banner: '/Assets/Services/AI & ML/ai-ml-banner-image.png',
    subtitle: 'Production-grade AI and machine learning, from strategy to MLOps.',
    intro: 'We build AI and ML solutions that fit your operations: secure by design, API-first and monitored in production.',
    flow: ['Data', 'Models', 'Integration', 'Monitoring'],
    capabilitiesTitle: 'What We Deliver',
    capabilities: [
      { title: 'AI/ML Strategy Consulting', text: 'Evidence-based guidance on where AI creates measurable value for your organization.' },
      { title: 'PoC & MVP Development', text: 'Prove feasibility on your data, then grow the idea into a working product.' },
      { title: 'Custom Model Development', text: 'Data preparation, training, multi-level testing and deployment for your specific task.' },
      { title: 'Custom AI Solutions', text: 'AI-powered web and mobile apps, engineered from interface to algorithm.' },
      { title: 'Integration & Deployment', text: 'Secure, versioned APIs, CI/CD and MLOps with drift monitoring.' },
      { title: 'Data Analysis & Visualization', text: 'Statistics, programming and visualization for evidence-backed decisions.' },
    ],
    process: {
      title: 'How We Work',
      steps: [
        { title: 'Discover', text: 'Goals, data and constraints.' },
        { title: 'Design', text: 'Architecture and success measures.' },
        { title: 'Build & Test', text: 'Models, APIs and evaluation.' },
        { title: 'Release', text: 'CI/CD into your systems.' },
        { title: 'Operate', text: 'Monitoring and improvement.' },
      ],
    },
    methods: {
      title: 'Technologies We Work With',
      items: [
        { title: 'Machine Learning', text: 'Insight from structured and unstructured data.' },
        { title: 'Deep Learning', text: 'Speech and image recognition, automation.' },
        { title: 'Predictive Analytics', text: 'Forward-looking insight into trends.' },
        { title: 'Natural Language Processing', text: 'Value from textual data.' },
        { title: 'Data Science', text: 'Advanced techniques for specific use cases.' },
        { title: 'Data Capture / OCR', text: 'Faster, more accurate document processing.' },
      ],
    },
    stack: {
      title: 'AI Technology Stack',
      groups: [
        { label: 'Language Models', items: [t('GPT', undefined, 'GPT', '#10A37F'), t('Llama', 'meta'), t('Gemini', 'gemini'), t('DeepSeek', 'deepseek')] },
        { label: 'AI Frameworks', items: [t('TensorFlow', 'tensorflow'), t('PyTorch', 'pytorch')] },
        { label: 'Generative AI Models', items: ['Large language models (proprietary and open-weight)', 'Transformer models', 'GANs'] },
        { label: 'Agentic AI', items: ['Tool calling', 'RAG', 'Multi-agent orchestration', 'MCP', 'Evaluation & observability'] },
        { label: 'Algorithms', items: ['Supervised / Unsupervised Learning', 'Clustering', 'Metric Learning', 'Few-shot Learning'] },
        { label: 'Neural Networks', items: ['CNN', 'RNN', 'Representation Learning', 'Manifold Learning', 'Variational Autoencoders', 'Bayesian Networks', 'Autoregressive Networks'] },
      ],
    },
    why: {
      title: 'Why Choose Zemantics?',
      items: [
        { title: 'Expert AI professionals', text: 'Specialists in machine learning, NLP and computer vision.' },
        { title: 'Extensive experience', text: 'From recommendation systems to decision-support tools.' },
        { title: 'Secure by design', text: 'Encryption, least-privilege access, audit logging and OWASP practices.' },
        { title: 'End-to-end delivery', text: 'Discovery to operation, integrated into your existing systems.' },
      ],
    },
    faq: [
      { q: 'What is agentic AI?', a: 'Agentic AI uses language models that plan steps, call tools and APIs, and complete tasks toward a goal, with guardrails and human approval for sensitive actions.' },
      { q: 'How do you take a model to production?', a: 'Through secure, versioned APIs, CI/CD, automated tests and MLOps monitoring for drift and quality.' },
      { q: 'How do you keep AI systems secure?', a: 'Encryption in transit and at rest, least-privilege access, secrets management, vulnerability scanning and audit logging.' },
    ],
    cta: 'Ready to put AI to work?',
  },

  dataengineering: {
    name: 'Data Engineering',
    banner: '/Assets/Services/Data Engineering/data-engineering-banner-image.png',
    subtitle: 'Governed, observable data foundations for analytics and AI.',
    intro: 'We prepare, validate and serve data so your analytics, models and agents can trust it.',
    flow: ['Ingest', 'Validate', 'Transform', 'Serve'],
    capabilitiesTitle: 'What We Deliver',
    capabilities: [
      { title: 'Data Gathering & Preprocessing', text: 'API, streaming and batch ingestion with feature engineering, quality checks and lineage.' },
      { title: 'Data Annotation', text: 'Labeled data that gives models reliable examples to learn from.' },
      { title: 'Algorithm Selection & Tuning', text: 'Exploratory analysis, experimentation and hyperparameter optimization.' },
      { title: 'Model Training & Validation', text: 'Validated with cross-validation, confusion matrices and ROC curves.' },
      { title: 'Model Evaluation', text: 'Continuous monitoring with precision, recall, F1, drift and data-quality alerts.' },
      { title: 'Outcome Assessment', text: 'Find where processes fall short and decide on evidence.' },
    ],
    process: {
      title: 'How We Work',
      steps: [
        { title: 'Assess', text: 'Sources, quality and gaps.' },
        { title: 'Design', text: 'Architecture and data contracts.' },
        { title: 'Build', text: 'Pipelines, tests and lineage.' },
        { title: 'Run', text: 'Monitoring and support.' },
      ],
    },
    methods: { title: 'Methods Our Data Scientists Use', items: TECHNIQUES },
    stack: { title: 'AI Technology Stack', groups: PLATFORM_STACK },
    faq: [
      { q: 'What does data engineering for AI include?', a: 'Ingestion, validation, transformation, annotation, lineage, monitoring and serving of governed data to analytics, models and agents.' },
      { q: 'How do you keep data quality high?', a: 'Automated quality tests, data contracts, lineage and drift alerts catch issues before they reach models or dashboards.' },
      { q: 'Which platforms do you work with?', a: 'AWS, Google Cloud and Azure, with tools such as Databricks, Airflow, Docker and Kubernetes.' },
    ],
    cta: 'Ready to build your data foundation?',
  },

  generativeai: {
    name: 'Generative AI',
    banner: '/Assets/Services/Generative AI/generative-ai-banner-image.png',
    subtitle: 'Generative and agentic AI, grounded in your data.',
    intro: 'We build generative AI that is accurate, evaluated and safe: RAG, domain-specific models and agents, with guardrails and human review.',
    flow: ['Your data', 'Retrieval', 'Model', 'Guardrails'],
    capabilitiesTitle: 'What We Deliver',
    capabilities: [
      { title: 'Domain-Specific Models', text: 'Choose the right foundation model, adapt it with your data and test it against your requirements.' },
      { title: 'Generative AI Consulting', text: 'An adoption strategy and the right solution for your use case.' },
      { title: 'Data Analysis', text: 'Patterns and recommendations from structured data and unstructured text, traceable to sources.' },
    ],
    process: {
      title: 'How We Build',
      steps: [
        { title: 'Requirement Assessment', text: 'Needs, objectives and constraints.' },
        { title: 'Strategy Building', text: 'Model choice and deployment plan.' },
        { title: 'Design & Development', text: 'Architecture and a tuned model.' },
        { title: 'Testing', text: 'Evaluation, red-teaming, guardrails.' },
        { title: 'Deployment & Optimization', text: 'CI/CD with observability.' },
        { title: 'Integration & Support', text: 'Fit into your ecosystem.' },
        { title: 'Upgrade & Maintenance', text: 'Regular upgrades keep it sharp.' },
      ],
    },
    methods: { title: 'Techniques We Use', items: TECHNIQUES },
    stack: { title: 'AI Technology Stack', groups: PLATFORM_STACK },
    faq: [
      { q: 'What is RAG?', a: 'Retrieval-augmented generation lets a model look up your documents before answering, so responses are current, specific and traceable to their sources.' },
      { q: 'How do you evaluate generative AI?', a: 'Automated test sets, regression suites and red-teaming measure quality and safety before and after each release.' },
      { q: 'Which models do you use?', a: 'Leading proprietary and open-weight language models, chosen per use case for accuracy, cost, latency and data requirements.' },
    ],
    cta: 'Ready to build with generative AI?',
  },

  softwareengineering: {
    name: 'Software Engineering',
    banner: '/Assets/Services/Software Engineering/software-engineering-banner-image.png',
    subtitle: 'Cloud-native, API-first software for SaaS and AI products.',
    intro: 'With 15+ years of experience and 175+ software products built, we engineer secure, scalable software to your use case, budget and timeline.',
    flow: ['Plan', 'Build', 'Test', 'Release', 'Operate'],
    capabilitiesTitle: 'What We Deliver',
    capabilities: [
      { title: 'Application Services', text: 'Design, testing, deployment and maintenance for web, mobile and desktop apps and their APIs.' },
      { title: 'Custom Software Development', text: 'Modular, API-first software built around your business process, with the integrations you need.' },
      { title: 'Testing & QA', text: 'Automated unit, integration, end-to-end, performance and security testing in the delivery pipeline.' },
      { title: 'UI/UX Design', text: 'Design systems, responsive layouts and accessibility to WCAG standards.' },
      { title: 'Cloud & DevOps', text: 'Cloud-native architecture, CI/CD and infrastructure as code.' },
      { title: 'Agile Development', text: 'Short, reviewable cycles and frequent, low-risk releases.' },
    ],
    process: {
      title: 'How We Work',
      steps: [
        { title: 'Plan', text: 'Scope, architecture and roadmap.' },
        { title: 'Design', text: 'Prototypes tested with users.' },
        { title: 'Build', text: 'Sprints with automated tests.' },
        { title: 'Release', text: 'Continuous, low-risk delivery.' },
        { title: 'Support', text: 'Monitor, maintain, evolve.' },
      ],
    },
    stack: {
      title: 'Technology Stack',
      groups: [
        { label: 'Front-end', items: [t('Angular', 'angular'), t('HTML5', 'html5'), t('React', 'react'), t('Bootstrap', 'bootstrap')] },
        { label: 'Back-end', items: [t('Node.js', 'nodejs'), t('Python', 'python'), t('Express', 'express'), t('Java', 'java'), t('Spring', 'spring'), t('Go', 'go')] },
        { label: 'Mobile App Development', items: [t('Swift', 'swift'), t('Ionic', 'ionic'), t('Kotlin', 'kotlin'), t('Flutter', 'flutter'), t('Xcode', 'xcode')] },
        { label: 'Databases', items: [t('MySQL', 'mysql'), t('Oracle', 'oracle'), t('MongoDB', 'mongodb')] },
        { label: 'Big Data', items: [t('Kafka', 'kafka'), t('MongoDB', 'mongodb'), t('DynamoDB', 'dynamodb'), t('Redshift', undefined, 'RS', '#8C4FFF'), t('DocumentDB', undefined, 'DB', '#3B48CC')] },
        { label: 'Servers & Serverless', items: [t('AWS', 'aws'), t('Google Cloud', 'googlecloud'), t('Azure', 'azure')] },
        { label: 'DevOps', items: [t('Kubernetes', 'kubernetes'), t('Jenkins', 'jenkins'), t('Chef', 'chef'), t('Maven', 'maven')] },
        { label: 'Project Management', items: [t('Jira', 'jira'), t('Confluence', 'confluence'), t('Asana', 'asana'), t('Trello', 'trello')] },
      ],
    },
    faq: [
      { q: 'What kind of software do you build?', a: 'Custom web, mobile and desktop applications, SaaS platforms and APIs, including AI-powered products.' },
      { q: 'How do you ensure quality?', a: 'Automated unit, integration, end-to-end, performance and security tests run in CI/CD, alongside accessibility checks.' },
      { q: 'How do projects run?', a: 'Agile sprints with frequent releases, as a dedicated team, team extension or project-based engagement.' },
    ],
    cta: 'Have a product to build?',
  },
};
